import { AxiosRequestHeaders } from 'axios';
import OrdersBackendService from './OrdersBackendService';
import CartApiRoutes from '../api/CartApiRoutes';
import { IAssignableVoucherInfo } from '../common/interfaces/vouchers.interface';
import TransformHelper from '../helpers/TransformHelper';
import ApplicationError from '../errors/application-error';
import LocalStorageHelper from '../helpers/LocalStorageHelper';
import CookieHelper from '../helpers/CookieHelper';

export default class CartApiService {
  private accessToken: string | null = null;
  private accessTokenExpireTime: Date | null = null;
  private ordersBackendService: OrdersBackendService;

  constructor(ordersBackendService: OrdersBackendService) {
    this.ordersBackendService = ordersBackendService;
  }

  private async ensureAccessToken(): Promise<void> {
    const currentTime = Date.now();
    const tokenExpireTime = this.accessTokenExpireTime?.getTime() ?? null;
    // if the token is present but about to expire soon (in a minute) or already expired, or the token is not present at all - acquire it
    let tokenExpired = true;
    let tokenExpiresSoon = true;
    if(tokenExpireTime != null) {
      tokenExpired = currentTime >= tokenExpireTime;
      if(!tokenExpired) {
        tokenExpiresSoon = (currentTime < tokenExpireTime) && (tokenExpireTime - currentTime <= 60000);
      }
    }
    if(
      (this.accessToken != null && (tokenExpired || tokenExpiresSoon))
      || this.accessToken == null
    ) {
      try {
        const response = await this.ordersBackendService.getAccessToken();
        this.accessToken = response.accessToken;
        this.accessTokenExpireTime = new Date(response.expiry);
      } catch (err) {
        if(err instanceof Error) {
          throw err;
        } else if(typeof err === `string`) {
          throw new Error(err);
        } else {
          throw new Error(`Unknown error state when getting cartapi access token`);
        }
      }
    }
  }
  private createHeaders(): AxiosRequestHeaders {
    return {
      Authorization: `Bearer ${this.accessToken}`,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      'x-rin-customer-number': LocalStorageHelper.getPersonalInfo()?.account.customerNumber.toString() || undefined,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      'x-rin-session': CookieHelper.getSession() || undefined,
    };
  }

  public async getVouchersAssignableToDownline(): Promise<IAssignableVoucherInfo[]> {
    await this.ensureAccessToken();
    if(!this.accessToken) {
      throw new ApplicationError(`No access token available`);
    }
    const response = await CartApiRoutes.getVouchersAssignableToDownline.fetch({
      headers: this.createHeaders(),
    });
    return TransformHelper.transformApiAssignableVoucherInfosToAssignableVoucherInfos(response.data);
  }

  public async assignVoucherToDownlineCustomer(customerNumber: number, voucherCode: string): Promise<IAssignableVoucherInfo[]> {
    await this.ensureAccessToken();
    if(!this.accessToken) {
      throw new ApplicationError(`No access token available`);
    }
    const response = await CartApiRoutes.assignVoucherToDownlineCustomer.fetch({
      data: {
        customerNumber,
      },
      headers: this.createHeaders(),
      params: {
        voucherCode,
      },
    });
    return TransformHelper.transformApiAssignableVoucherInfosToAssignableVoucherInfos(response.data);
  }
}
